import { toJS } from "mobx";
import Quill from "quill";

import { NodeBE } from "src/api/flowTypes";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { EventConfigEnriched } from "src/clients/features-control/api";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import {
  ENTITY_EVENT_MENTION_CLOSE,
  ENTITY_EVENT_MENTION_OPEN,
  NODE_MENTION_CLOSE,
  NODE_MENTION_OPEN,
  ResourceType,
} from "src/copilot/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { GraphTransform } from "src/utils/GraphUtils";

type Mention = {
  value: string;
  id: string;
};

export type EntityMentionResource = EntitySchemaResource & Mention;
export type EventMentionResource = EventConfigEnriched & Mention;
export type MentionedResource =
  | EntityMentionResource
  | EventMentionResource
  | BeMappedNode;

export type MentionItem = MentionedResource & {
  id: string;
  value: string;
  index: number;
};

export const isEntity = (
  item: MentionedResource,
): item is EntityMentionResource => {
  return "_id" in item && "_display_name_singular" in item;
};

export const isEvent = (
  item: MentionedResource,
): item is EventMentionResource => {
  return "display_name_singular" in item;
};

export const isNode = (item: MentionedResource): item is BeMappedNode => {
  return "type" in item && "data" in item;
};

const getMentionsData = (resource: MentionedResource[]) => {
  return resource
    .filter((item) => {
      // Filter out non-mentionable node types
      if (isNode(item)) {
        return ![
          NODE_TYPE.INPUT_NODE,
          NODE_TYPE.OUTPUT_NODE,
          NODE_TYPE.SPLIT_BRANCH_NODE_V2,
          NODE_TYPE.SPLIT_MERGE_NODE,
        ].includes(item.type);
      }
      return true; // Include all entities and events
    })
    .map((item) => {
      if (isEntity(item)) {
        return {
          ...item,
          id: item._id,
          value: item._display_name_singular,
        };
      } else if (isEvent(item)) {
        return {
          ...item,
          id: item.display_name_singular,
          value: item.display_name_singular,
        };
      } else {
        // Node
        return {
          ...item,
          value: item.data.label,
        };
      }
    });
};

export const filterQuillValues = (
  searchTerm: string,
  renderList: (values: MentionedResource[], searchTerm: string) => void,
  resource: MentionedResource[],
) => {
  const mentionsData = getMentionsData(resource);
  const normalizedSearchTerm = searchTerm.trim();

  if (normalizedSearchTerm.length === 0) {
    renderList(mentionsData, searchTerm);
  } else {
    const matches = [];
    for (let i = 0; i < mentionsData.length; i++)
      if (
        ~mentionsData[i].value
          .toLowerCase()
          .indexOf(normalizedSearchTerm.toLowerCase())
      )
        matches.push(mentionsData[i]);
    renderList(matches, normalizedSearchTerm);
  }
};

const getNodeDefinition = (nodeId: string, nodesArray: BeMappedNode[]) => {
  const node = nodesArray.find((node) => node.id === nodeId);
  return node ? GraphTransform.nodeReverseMapper(node) : null;
};

const getMentionedTag = (resourceType: ResourceType) => {
  if (
    resourceType ===
    FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
  ) {
    return [ENTITY_EVENT_MENTION_OPEN, ENTITY_EVENT_MENTION_CLOSE];
  } else {
    return [NODE_MENTION_OPEN, NODE_MENTION_CLOSE];
  }
};

export const getQuillContent = (
  quill: Nullable<Quill>,
  resource: MentionedResource[],
  resourceType: ResourceType,
) => {
  const mentionedResources: (
    | EntitySchemaResource
    | EventConfigEnriched
    | NodeBE
  )[] = [];

  const content = quill
    ?.getContents()
    .ops.map((op) => {
      if (typeof op.insert === "object" && op.insert?.mention) {
        const { value, id } = op.insert.mention as Mention;

        const foundResource = resource.find((item) => item.id === id);

        const [openTag, closeTag] = getMentionedTag(resourceType);

        const data =
          resourceType ===
          FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
            ? foundResource
            : getNodeDefinition(id, resource as BeMappedNode[]);

        if (data) {
          mentionedResources.push(
            data as EntitySchemaResource | EventConfigEnriched | NodeBE,
          );
        }

        return openTag + value + closeTag;
      }
      return op.insert;
    })
    .join("");

  return { content, mentionedResources };
};

export const quillOnSelect = (
  item: MentionItem,
  insertItem: (item: MentionItem) => void,
  resource: MentionedResource[],
) => {
  const currentResource = resource.find((r) => r.id === item.id);

  // Convert MobX observable to plain JS object before cloning
  insertItem({ ...item, ...structuredClone(toJS(currentResource)) });
};
